'use client';

import type { ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { FontSizes, FontWeights, Text } from '@calm-web/design-system';

import paymentElementsFormRefreshMessages from '@/components/purchase/PaymentElementsFormRefresh/messages';
import planSkuToggleMessages from '@/components/purchase/PlanSkuToggle/messages';
import planSkuItemMessages from '@/components/purchase/PlanSkuToggle/PlanSkuItem/messages';
import { useAnalytics } from '@/hooks/analytics';
import genericMessages from '@/messages/messages';
import { JSONObject } from '@/utils/types';

import { useFormatPrice } from '../hooks';
import { PurchaseFormSectionWrapper } from '../PaymentMethod/styles';
import { useSisu } from '../Provider';
import TotalDueToday from '../TotalDueToday';
import { isFormattedMessage, type MessageNode } from '../utils';
import messages from './messages';
import { SkuInterior, SkuList, SkuListItem, SkuRadio, TrialBadge } from './styles';

export type PaymentPlanProps = {
	isOrdered?: boolean;

	title?: MessageNode | null;

	totalDueToday?: ReactElement;

	subtitle?: MessageNode | null;
};

export default function PaymentPlan({
	isOrdered,
	title,
	totalDueToday = <TotalDueToday />,
	subtitle,
}: PaymentPlanProps) {
	const { formatMessage } = useIntl();
	const formatPrice = useFormatPrice();
	const { isSelectedSku, setSelectedSku, skus } = useSisu();
	const { logEvent } = useAnalytics();

	if (typeof isOrdered === 'undefined') {
		isOrdered = skus.length > 1;
	}

	if (typeof title === 'undefined') {
		title = formatMessage(
			skus.length > 1
				? planSkuToggleMessages.confirmPlan
				: paymentElementsFormRefreshMessages.confirmPlanNoToggle,
		);
		if (isOrdered) {
			title = `1. ${title}`;
		}
	}

	if (typeof subtitle === 'undefined') {
		subtitle = formatMessage(planSkuToggleMessages.confirmSubtitle);
	}

	return (
		<PurchaseFormSectionWrapper>
			{title && isFormattedMessage(title) ? (
				<Text data-testid="sisu-payment-plan-title" el="p" size={FontSizes.lg} weight={FontWeights.Medium}>
					{title}
				</Text>
			) : (
				title
			)}
			{subtitle && isFormattedMessage(subtitle) ? (
				<Text data-testid="sisu-payment-plan-subtitle" el="p" size={FontSizes.base}>
					{subtitle}
				</Text>
			) : (
				subtitle
			)}
			{skus.length > 1 && (
				<SkuList>
					{skus.map(sku => {
						const active = isSelectedSku(sku);
						return (
							<SkuListItem key={sku.rawSKU}>
								<SkuRadio
									role="radio"
									name="plan sku"
									isActive={active}
									onPress={() => {
										const eventProps: JSONObject = {};

										if (sku.duration === 'year') {
											eventProps.plan_id = 'yearly';
										} else if (sku.duration === 'month') {
											eventProps.plan_id = 'monthly';
										}

										logEvent({
											eventName: 'Subscription Plan : Selected',
											eventProps,
										});
										setSelectedSku(sku);
									}}
									activeBackgroundColor="white"
									inactiveBackgroundColor="white"
									aria-checked={active}
									focusRingVariant="dark"
									borderWidth={2}
									borderColor={active ? 'focusRingDark' : 'inputBgDisabled'}
								>
									<SkuInterior>
										<div>
											<Text size={FontSizes.base} weight={FontWeights.Demi}>
												{sku.duration === 'year' && formatMessage(messages.yearly)}
												{sku.duration === 'month' && formatMessage(messages.monthly)}
											</Text>
											{sku.basePrice && <br />}
											{sku.duration === 'year' && (
												<Text size={FontSizes.sm}>
													{`${formatPrice(sku.finalPrice, sku.currency)}/${formatMessage(
														planSkuToggleMessages.yearlyAbbreviation,
													)}`}
												</Text>
											)}
										</div>
										<div>
											{sku.duration === 'month' &&
												`${formatPrice(sku.finalPrice, sku.currency)}/${formatMessage(
													planSkuItemMessages.monthlyAbbreviation,
												)}`}
											{sku.duration === 'year' &&
												`${formatPrice(sku.finalPrice / 12, sku.currency)}/${formatMessage(
													planSkuItemMessages.monthlyAbbreviation,
												)}`}
										</div>
									</SkuInterior>
									{((typeof sku.trialDuration?.days === 'number' && sku.trialDuration.days > 0) ||
										(typeof sku.trialDuration?.months === 'number' && sku.trialDuration.months > 0)) && (
										<TrialBadge>
											<Text size={FontSizes.sm} color="white">
												{typeof sku.trialDuration?.days === 'number' &&
													sku.trialDuration.days > 0 &&
													formatMessage(genericMessages.purchaseTitleFreetrial, {
														duration: sku.trialDuration.days,
														quantity: sku.trialDuration.days,
													})}
												{typeof sku.trialDuration?.months === 'number' &&
													sku.trialDuration.months > 0 &&
													formatMessage(genericMessages.purchaseTitleFreetrialMonth, {
														duration: sku.trialDuration.months,
														quantity: sku.trialDuration.months,
													})}
											</Text>
										</TrialBadge>
									)}
								</SkuRadio>
							</SkuListItem>
						);
					})}
				</SkuList>
			)}
			{totalDueToday}
		</PurchaseFormSectionWrapper>
	);
}
